/**
 * Wire contracts for the /admin/users/* endpoints (PR #5a.3).
 *
 * A user is a person authenticated against CORTEX. Per
 * `docs/USER-ENTITY-MODEL.md` §3b a user belongs to exactly one entity.
 *
 * Authentication is owned by Cloudflare Access (Option B, PR #4b) — the
 * admin surface here never sees passwords or OTP secrets. Creating a
 * user is really "provision the CORTEX record"; the Cloudflare identity
 * (`cfAccessSubject`) is bound automatically on first sign-in. Two
 * fields are intentionally NOT editable in v1:
 *
 *   - `email`    — it is the key Cloudflare binds against.
 *   - `entityId` — moving a user between entities would invalidate role
 *                  assignments and facility scope; do it by re-provisioning.
 *
 * Authority limits are added in PR #5c.
 */

import { z } from 'zod';

const LifecycleStatusSchema = z.enum(['Active', 'Suspended']);

const IdentityProviderSchema = z.enum(['cloudflare-otp', 'entra-id', 'google', 'service-account']);

/**
 * The effective bind authority for a user, as resolved by `permissionRepo`.
 * Returned on the user detail endpoint so the Admin Module can display it.
 */
export const UserAuthoritySchema = z.object({
  /** Numeric string (e.g. "5000000.00") or null = no bind authority. */
  bindLimit: z.string().nullable(),
  /** Decimal fraction (e.g. "0.20") or null = no discretionary range authority. */
  discretionaryRangePct: z.string().nullable(),
  /** True when the values come from a per-user override rather than a role default. */
  hasUserOverride: z.boolean(),
  /** The raw override row, if one exists. NULL when no override is set. */
  override: z
    .object({
      bindLimitOverride: z.string().nullable(),
      discretionaryRangeOverride: z.string().nullable(),
      notes: z.string(),
      updatedAt: z.string().datetime(),
      updatedByUserId: z.string().uuid().nullable(),
    })
    .nullable(),
});

export const UserSchema = z.object({
  id: z.string().uuid(),
  entityId: z.string().uuid(),
  entityName: z.string().min(1),
  entityTypeSlug: z.string().min(1),

  email: z.string().email(),
  displayName: z.string().min(1),

  /** NULL until the user signs in through Cloudflare for the first time. */
  cfAccessSubject: z.string().nullable(),
  identityProvider: IdentityProviderSchema,

  status: LifecycleStatusSchema,
  /** ISO timestamp of the last authenticated request; NULL if never signed in. */
  lastLoginAt: z.string().datetime().nullable(),
});

export const UserCreateRequestSchema = z.object({
  entityId: z.string().uuid(),
  email: z.string().trim().toLowerCase().email().max(254),
  displayName: z.string().trim().min(1, 'required').max(200),
  status: LifecycleStatusSchema.optional(),
});

/**
 * Update is limited to the two mutable fields. `email` and `entityId`
 * are immutable in v1 (see file header).
 */
export const UserUpdateRequestSchema = z.object({
  displayName: z.string().trim().min(1, 'required').max(200).optional(),
  status: LifecycleStatusSchema.optional(),
});

export const UserListQuerySchema = z.object({
  entityId: z.string().uuid().optional(),
  status: LifecycleStatusSchema.optional(),
  search: z.string().trim().min(1).max(200).optional(),
});

/**
 * Extended user record returned only by `GET /admin/users/:id`.
 * Includes resolved authority limits which require a separate DB query
 * and are too expensive to load for every row in the list.
 */
export const UserDetailSchema = UserSchema.extend({
  authority: UserAuthoritySchema,
});

export const UserListResponseSchema = z.object({
  users: z.array(UserSchema),
});

/** Used by create/update responses — authority is not re-fetched on write paths. */
export const UserResponseSchema = z.object({
  user: UserSchema,
});

/** Used by GET /admin/users/:id — includes resolved authority limits. */
export const UserDetailResponseSchema = z.object({
  user: UserDetailSchema,
});

/**
 * Body for `PUT /admin/users/:id/authority`.
 * Both fields may be null to clear their respective overrides.
 * `notes` is required (non-empty) when setting any non-null limit.
 */
export const UserAuthorityOverrideRequestSchema = z.object({
  /**
   * ZAR amount as a positive number. NULL = clear the bind limit override.
   * When non-null must be > 0.
   */
  bindLimitOverride: z.number().positive().nullable(),
  /**
   * Decimal fraction 0–1 (e.g. 0.20 for ±20%). NULL = clear discretionary
   * range override. When non-null must be in range [0, 1].
   */
  discretionaryRangeOverride: z.number().min(0).max(1).nullable(),
  /** Mandatory reason when setting any non-null limit. */
  notes: z.string().trim().max(500),
});

export const UserAuthorityOverrideResponseSchema = z.object({
  authority: UserAuthoritySchema,
});

export type User = z.infer<typeof UserSchema>;
export type UserDetail = z.infer<typeof UserDetailSchema>;
export type UserAuthority = z.infer<typeof UserAuthoritySchema>;
export type UserDetailResponse = z.infer<typeof UserDetailResponseSchema>;
export type UserAuthorityOverrideRequest = z.infer<typeof UserAuthorityOverrideRequestSchema>;
export type UserAuthorityOverrideResponse = z.infer<typeof UserAuthorityOverrideResponseSchema>;
export type UserCreateRequest = z.infer<typeof UserCreateRequestSchema>;
export type UserUpdateRequest = z.infer<typeof UserUpdateRequestSchema>;
export type UserListQuery = z.infer<typeof UserListQuerySchema>;
export type UserListResponse = z.infer<typeof UserListResponseSchema>;
export type UserResponse = z.infer<typeof UserResponseSchema>;
